<script lang="ts">
  import { onMount } from "svelte";
  import { app } from "../state/app.svelte";
  import { isConfigComplete } from "../state/config";
  import { i18n } from "../i18n/i18n.svelte";
  import ChatView from "./components/ChatView.svelte";
  import CitationBubble from "./components/CitationBubble.svelte";
  import ExaWebWarning from "./components/ExaWebWarning.svelte";
  import ConfigView from "./components/ConfigView.svelte";
  import ConversationList from "./components/ConversationList.svelte";
  import DocLibrary from "./components/DocLibrary.svelte";
  import Header from "./components/Header.svelte";
  import ModelNotice from "./components/ModelNotice.svelte";
  import TutorialModal from "./components/TutorialModal.svelte";

  // Ctrl+scroll zoom. The whole UI is sized in `rem`, so scaling the root
  // font-size scales every component proportionally. Persisted across reloads.
  const fr = {
    loading: "Chargement…",
    back: "← Retour",
    needsConfig: "Configure les clés API pour activer l'agent.",
  };
  const en: typeof fr = {
    loading: "Loading…",
    back: "← Back",
    needsConfig: "Complete the settings to enable the agent.",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  const FONT_SCALE_KEY = "app.fontScale";
  const FONT_SCALE_MIN = 0.6;
  const FONT_SCALE_MAX = 2.5;
  const BASE_FONT_PX = 16;

  function applyFontScale(scale: number): void {
    document.documentElement.style.fontSize = `${BASE_FONT_PX * scale}px`;
  }

  function onWheel(e: WheelEvent): void {
    if (!e.ctrlKey) return;
    e.preventDefault();
    const current = parseFloat(localStorage.getItem(FONT_SCALE_KEY) ?? "1") || 1;
    // deltaY > 0 = scroll down = zoom out, like browsers
    const step = e.deltaY > 0 ? -0.1 : 0.1;
    const next = Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, current + step));
    localStorage.setItem(FONT_SCALE_KEY, String(next));
    applyFontScale(next);
  }

  onMount(() => {
    const stored = parseFloat(localStorage.getItem(FONT_SCALE_KEY) ?? "1") || 1;
    applyFontScale(stored);
    window.addEventListener("wheel", onWheel, { passive: false });
    app.init();
    return () => window.removeEventListener("wheel", onWheel);
  });

  // OIDC is mandatory — every backend call (parse, embeddings, chat) goes
  // through the server now, so without an OIDC session nothing works. We
  // still let the user dismiss the panel (via Retour) so they aren't trapped
  // when they want to e.g. inspect the About tab without logging in.
  // `authReconnecting` suppresses the panel while a silent re-login is in
  // flight: the session is very likely about to come back, and flashing a
  // sign-in screen for a second is exactly the disconnection the user feels.
  const needsConfig = $derived(
    app.configReady &&
      !app.authReconnecting &&
      (!isConfigComplete(app.config) || !app.oidcSession),
  );
  const showConfigView = $derived(
    app.showConfig || (needsConfig && !app.configDismissed),
  );

  // First launch: show the tutorial once the app is actually usable — i.e. not
  // while the sign-in / config panel is up, where a modal would only be in the
  // way. `openTutorialFirstRun` is a no-op after the first time and persists
  // the flag itself.
  // Same moment for the model-change notice, which yields to the tutorial.
  $effect(() => {
    if (app.configReady && !showConfigView) {
      void app.openTutorialFirstRun().then(() => app.openModelNoticeOnStartup());
    }
  });
</script>

<main>
  {#if !app.configReady}
    <div class="loading">{t.loading}</div>
  {:else if showConfigView}
    <div class="config-wrap">
      <button
        class="back"
        onclick={() => {
          app.showConfig = false;
          app.configDismissed = true;
        }}
      >{t.back}</button>
      {#if needsConfig}
        <p class="note">{t.needsConfig}</p>
      {/if}
      <ConfigView />
    </div>
  {:else}
    <Header />
    <div class="view">
      {#if app.view === "chat"}
        <ChatView />
      {:else if app.view === "docs"}
        <DocLibrary />
      {:else if app.view === "history"}
        <ConversationList />
      {/if}
    </div>
  {/if}

  <CitationBubble />
  <ExaWebWarning />
  <TutorialModal />
  <ModelNotice />
</main>

<style>
  :global(html, body) {
    height: 100%;
    margin: 0;
  }

  /* Elements sets Arial on `body` and lets everything inherit; the tokens in
     epfl.css hold the stack. Sizes stay at the app's compact scale rather than
     the EPFL type scale (h1 at 48px) — that scale is meant for full-width
     pages, not a ~400px side panel. */
  :global(body) {
    font-family: var(--epfl-font);
    color: var(--epfl-text);
    background: var(--epfl-gray-50);
    overflow: hidden;
  }

  /* Link treatment from Elements: underlined in body text, accent on hover. */
  :global(a) {
    color: var(--epfl-text);
    text-decoration: underline;
  }

  :global(a:hover) {
    color: var(--epfl-accent);
  }

  :global(*) {
    box-sizing: border-box;
  }

  main {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: var(--epfl-gray-50);
  }

  .loading {
    padding: 2rem;
    text-align: center;
    color: var(--epfl-gray-400);
  }

  .view {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .config-wrap {
    flex: 1;
    overflow-y: auto;
  }

  .back {
    margin: 0.75rem 1rem 0;
    background: none;
    border: 1px solid var(--epfl-gray-200);
    padding: 0.3rem 0.6rem;
    border-radius: var(--epfl-radius);
    font-size: 0.75rem;
    cursor: pointer;
    font-family: inherit;
    color: var(--epfl-gray-600);
  }

  .back:hover {
    background: var(--epfl-gray-100);
  }

  .note {
    padding: 0.75rem 1rem 0;
    margin: 0;
    font-size: 0.85rem;
    color: var(--epfl-gray-600);
  }
</style>
